import React, { useEffect, useMemo, useState } from "react";
import { FaExternalLinkAlt, FaChartLine, FaChevronDown, FaChevronUp, FaUser, FaGift } from "react-icons/fa";
import { agentSolidColour, agentTextOn } from "../utils/agentColour";
import BrandedSendControl from "./BrandedSendControl";
import SignInModal from "./SignInModal";

// Sticky promo that invites a signed-in agent (identified by their
// param_token) to open their own agency-branded insights hub. It only ever
// renders when a param_token is present, so anonymous marketing-site visitors
// never see it. It's a full-width horizontal banner pinned to the bottom of
// the viewport on every screen size, and is not dismissible.
//
// The hub itself lives on the consumer site:
//   https://www.homeprice.site/property-search-live?agent=<slug>
// We resolve <slug> from /activate-trial-info, which (unlike the JWT-gated
// onboarding endpoint) resolves an agent purely from the param_token. Once we
// know the agent we deep-link straight there in a new tab; until then the CTA
// shows a gentle loading state.

const HUB_BASE = "https://www.homeprice.site/property-search-live";

const REFERRAL_STORAGE_KEY = "referral_r";
const REFERRAL_TOKEN_KEY = "referral_t";

const safeLocalGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};
const safeLocalSet = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
};

export default function BrandedHubPromo() {
  // Capture ?r= (and optional ?t=) on every navigation so a marketing-site
  // visitor who arrived from a referral link keeps the offer attribution as
  // they click through pages. `t=` is the existing short-token used by the
  // blog newsletter / SMS campaigns — App.js's TokenIngest also persists it
  // as `param_token` once /resolve-sms-token returns the encrypted blob.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const r = (url.searchParams.get("r") || "").trim().toUpperCase();
      const t = url.searchParams.get("t") || "";
      if (r && /^[A-Z0-9]{4,16}$/.test(r)) {
        safeLocalSet(REFERRAL_STORAGE_KEY, r);
        if (t) safeLocalSet(REFERRAL_TOKEN_KEY, t);
      }
    } catch { /* ignore */ }
  }, []);

  const hasToken = !!safeLocalGet("param_token");
  const referralCode = safeLocalGet(REFERRAL_STORAGE_KEY) || "";
  const hasReferral = !!referralCode;

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [mounted, setMounted] = useState(false); // drives the entrance animation
  const [expanded, setExpanded] = useState(true); // minimise/expand (mobile + desktop)
  const [channel, setChannel] = useState("email"); // sample channel toggle
  const [slug, setSlug] = useState("");
  const [branding, setBranding] = useState(null); // { logoUrl, colour, agencyName, agentName, profileImage }
  const [resolved, setResolved] = useState(false); // resolver responded
  const [referrer, setReferrer] = useState(null);  // { agent_name, agency_name, logo_url, agent_colour, agent_profile_image }
  const [showSignIn, setShowSignIn] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Resolve the agent behind the token. We use /activate-trial-info because,
  // unlike /agent-onboarding-info (which authenticates the logged-in JWT), it
  // resolves an agent purely from the param_token — and a marketing-site
  // visitor who arrived from a campaign link has a param_token but no JWT.
  // It returns landing_page_slug plus branding (logo, agency name, colour).
  useEffect(() => {
    if (!hasToken) return;
    let cancelled = false;

    const token = safeLocalGet("param_token");
    if (!token) {
      setResolved(true);
      return;
    }

    fetch(`${window.api_host}/activate-trial-info`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !data || data.status !== "success") return;
        if (data.landing_page_slug) setSlug(data.landing_page_slug);
        setBranding({
          logoUrl: data.logo_url || "",
          colour: data.agent_colour || "",
          agencyName: data.agency_name || "",
          agentName: data.agent_name || "",
          profileImage: data.agent_profile_image || "",
          agencyId: data.agency_id || "",
          agentId: data.agent_id || "",
          agentEmail: data.agent_email || data.email || "",
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setResolved(true);
      });

    return () => {
      cancelled = true;
    };
  }, [hasToken]);

  // Fetch the referrer's branding when the visitor arrived with ?r=CODE so
  // the banner can show "Invited by <referring agent>".
  useEffect(() => {
    if (!hasReferral) return;
    let cancelled = false;
    fetch(`${window.api_host}/referral/info`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ r: referralCode }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !data || data.status !== "success") return;
        setReferrer(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [hasReferral, referralCode]);

  // Trigger the slide-in on the next frame after first paint.
  useEffect(() => {
    if (!hasToken && !hasReferral) return;
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, [hasToken, hasReferral]);

  const hubUrl = useMemo(() => {
    if (!slug) return HUB_BASE;
    const params = new URLSearchParams({ agent: slug, campaign: "vendorleads" });
    const token = safeLocalGet("param_token");
    if (token) params.set("param_token", token);
    return `${HUB_BASE}?${params.toString()}`;
  }, [slug]);

  if (!hasToken && !hasReferral) return null;

  // When the visitor arrived via a referral link but doesn't have a hub-token,
  // we brand the banner with the *referring* agent's details. When they have
  // a hub-token (own marketing campaign), we use that agent's branding; the
  // referral content is shown alongside as an extra CTA.
  const accentSource = (branding && branding.colour)
    || (referrer && referrer.agent_colour)
    || "#10b981";
  const accent = agentSolidColour(accentSource);
  const accentText = agentTextOn();
  const agencyName = (branding && branding.agencyName) || (referrer && referrer.agency_name) || "";
  const agentName = (branding && branding.agentName) || (referrer && referrer.agent_name) || "";
  const profileImage = (branding && branding.profileImage) || (referrer && referrer.agent_profile_image) || "";
  const knowAgent = !!slug;
  const ctaDisabled = hasToken && !resolved && !knowAgent; // still resolving the hub link

  const initials = (agentName || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

  // Round agent avatar — photo if we have one, otherwise initials/icon.
  const avatar = (size) =>
    profileImage ? (
      <img
        src={profileImage}
        alt={agentName || "Agent"}
        style={{
          height: size,
          width: size,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
          border: `2px solid ${accent}`,
        }}
      />
    ) : (
      <div
        style={{
          height: size,
          width: size,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${accent}, #3b82f6)`,
          color: accentText,
          fontSize: size * 0.4,
          fontWeight: 700,
        }}
      >
        {initials || <FaUser size={size * 0.45} />}
      </div>
    );

  // Send-yourself-a-sample block: the two property-update channels.
  // Only rendered once branding has resolved (so agencyId/agentId are present);
  // on mobile, only shown when the widget is expanded.
  const sendBranding = branding && {
    colour: branding.colour,
    agencyId: branding.agencyId,
    agentId: branding.agentId,
    agentEmail: branding.agentEmail,
  };
  const isEmail = channel === "email";
  const channelToggle = (
    <div
      role="tablist"
      aria-label="Sample channel"
      style={{
        display: "inline-flex",
        background: "#f1f5f9",
        borderRadius: 8,
        padding: 3,
        flexShrink: 0,
      }}
    >
      {[
        { key: "email", label: "Email" },
        { key: "sms", label: "SMS" },
      ].map((opt) => {
        const active = channel === opt.key;
        return (
          <button
            key={opt.key}
            role="tab"
            aria-selected={active}
            onClick={() => setChannel(opt.key)}
            data-track-event="true"
            style={{
              border: "none",
              background: active ? "#fff" : "transparent",
              color: active ? "#0f172a" : "#64748b",
              fontWeight: 700,
              fontSize: 12,
              padding: "6px 12px",
              borderRadius: 6,
              cursor: "pointer",
              boxShadow: active ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
  const samplesBlock = resolved && knowAgent && branding ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Send yourself a sample property update
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", paddingBottom: 22 }}>
        {channelToggle}
        <div style={{ width: "100%", maxWidth: 320, minWidth: 0 }}>
          {isEmail ? (
            <BrandedSendControl
              key="email"
              channel="email"
              endpoint="send-property-report"
              reportType="property_update"
              branding={sendBranding}
              slug={slug}
              placeholder="you@email.com"
              compact
              floatingMessage
              source="branded_hub_promo"
            />
          ) : (
            <BrandedSendControl
              key="sms"
              channel="sms"
              endpoint="send-property-update-sms-sample"
              branding={sendBranding}
              slug={slug}
              placeholder="0421 346 175"
              compact
              floatingMessage
              source="branded_hub_promo"
            />
          )}
        </div>
      </div>
    </div>
  ) : null;

  // Headline + subtext depend on which params we landed with.
  //   t only: existing "view your branded insights hub"
  //   r only: "$100 SMS credit on your first month"
  //   both:   SMS offer headline + branded-hub subtext
  const headline = hasReferral
    ? "$100 SMS credit on your first month"
    : "View your branded insights hub";
  const referrerName = referrer && (referrer.agent_first_name || (referrer.agent_name || "").split(" ")[0]);
  const referrerLabel = referrerName
    ? `${referrerName}${referrer && referrer.agency_name ? ` from ${referrer.agency_name}` : ""}`
    : null;
  const subtext = (() => {
    if (hasReferral && hasToken) {
      return referrerLabel
        ? `Invited by ${referrerLabel}. Claim your credit or see the hub already branded for you.`
        : "Claim your credit, or see the agency-branded property insights hub carrying your branding.";
    }
    if (hasReferral) {
      return referrerLabel
        ? `${referrerLabel} invited you to try Vendorleads — claim your credit when you sign up.`
        : "You were invited to try Vendorleads — claim your $100 SMS credit when you sign up.";
    }
    return agencyName
      ? `See the live property insights hub — branded for ${agencyName}.`
      : "See the agency-branded property insights hub, carrying your branding.";
  })();

  // CTA(s):
  //   r only:    Claim my credit (opens sign-in modal via vl-open-signin)
  //   t only:    View my hub (existing)
  //   both:      Claim my credit + View my hub
  const handleClaim = () => {
    setShowSignIn(true);
    // Also dispatch the global event so any page-level listeners (e.g. Landing
    // already wires one up) stay in sync if they care.
    try { window.dispatchEvent(new CustomEvent("vl-open-signin")); } catch { /* ignore */ }
  };

  const claimCta = (
    <button
      type="button"
      onClick={handleClaim}
      data-track-event="true"
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        gap: 8, flexShrink: 0, border: "none", cursor: "pointer",
        background: `linear-gradient(135deg, ${accent}, #3b82f6)`,
        color: accentText, fontWeight: 700, fontSize: 14,
        padding: isMobile ? "11px 18px" : "12px 22px",
        borderRadius: 10, whiteSpace: "nowrap",
        boxShadow: "0 6px 18px rgba(16,185,129,0.35)",
      }}
    >
      <FaGift size={12} /> {isMobile ? "Claim $100" : "Claim my $100 credit"}
    </button>
  );

  const hubCta = (
    <a
      href={hubUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-disabled={ctaDisabled}
      onClick={(e) => { if (ctaDisabled) e.preventDefault(); }}
      data-track-event="true"
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        gap: 8, flexShrink: 0,
        background: ctaDisabled ? "#cbd5e1" : (hasReferral ? "#fff" : `linear-gradient(135deg, ${accent}, #3b82f6)`),
        color: ctaDisabled ? "#64748b" : (hasReferral ? "#0f172a" : accentText),
        border: hasReferral ? `1px solid ${accent}` : "none",
        textDecoration: "none", fontWeight: 700, fontSize: 14,
        padding: isMobile ? "11px 18px" : "12px 22px",
        borderRadius: 10, whiteSpace: "nowrap",
        cursor: ctaDisabled ? "default" : "pointer",
        boxShadow: ctaDisabled ? "none" : (hasReferral ? "none" : "0 6px 18px rgba(16,185,129,0.35)"),
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(e) => {
        if (ctaDisabled || hasReferral) return;
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 8px 22px rgba(16,185,129,0.45)";
      }}
      onMouseLeave={(e) => {
        if (ctaDisabled || hasReferral) return;
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 6px 18px rgba(16,185,129,0.35)";
      }}
    >
      {ctaDisabled ? (isMobile ? "Loading…" : "Loading your hub…") : isMobile ? "View hub" : "View my hub"}
      {!ctaDisabled && !isMobile && <FaExternalLinkAlt size={11} />}
    </a>
  );

  // Single primary CTA (mobile minimised + desktop minimised states use this),
  // both buttons grouped for the expanded states.
  const cta = hasReferral ? claimCta : hubCta;
  const ctaGroup = (hasReferral && hasToken) ? (
    <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
      {claimCta}
      {hubCta}
    </div>
  ) : cta;

  // Agency logo / icon badge — shown on desktop only, where there's room.
  const logoUrl = (branding && branding.logoUrl) || (referrer && referrer.logo_url) || "";
  const logoBadge = logoUrl ? (
    <img
      src={logoUrl}
      alt={agencyName || "Agency logo"}
      style={{
        height: 64,
        width: "auto",
        maxWidth: 160,
        objectFit: "contain",
        borderRadius: 10,
        background: "#fff",
        padding: 6,
        flexShrink: 0,
        boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
      }}
    />
  ) : (
    <div
      style={{
        height: 40,
        width: 40,
        borderRadius: 10,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${accent}, #3b82f6)`,
        color: accentText,
      }}
    >
      <FaChartLine size={18} />
    </div>
  );

  // Avatar + name, used in the desktop banner and the mobile expanded header.
  const profileGroup = (avatarSize) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      {avatar(avatarSize)}
      {(agentName || agencyName) && (
        <div style={{ minWidth: 0 }}>
          {agentName && (
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {agentName}
            </div>
          )}
          {agencyName && (
            <div
              style={{
                fontSize: 12,
                color: "#64748b",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {agencyName}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Circular minimise / expand toggle (mobile only).
  const toggleBtn = (
    <button
      onClick={() => setExpanded((v) => !v)}
      aria-label={expanded ? "Minimise" : "Expand"}
      aria-expanded={expanded}
      data-track-event="false"
      style={{
        width: 30,
        height: 30,
        flexShrink: 0,
        borderRadius: "50%",
        border: "1px solid #e2e8f0",
        background: "#fff",
        color: "#64748b",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        lineHeight: 0,
      }}
    >
      {expanded ? <FaChevronDown size={12} /> : <FaChevronUp size={12} />}
    </button>
  );

  const headlineEl = (
    <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>
      {headline}
    </div>
  );
  const subtextEl = (
    <div
      style={{
        fontSize: isMobile ? 12.5 : 13.5,
        color: "#64748b",
        lineHeight: 1.35,
        marginTop: 2,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
      }}
    >
      {subtext}
    </div>
  );

  const wrapperStyle = {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1300,
    background: "#ffffff",
    borderTop: "3px solid transparent",
    borderImage: `linear-gradient(90deg, ${accent}, #3b82f6) 1`,
    boxShadow: "0 -6px 24px rgba(15,23,42,0.16)",
    padding: isMobile
      ? "12px 14px calc(12px + env(safe-area-inset-bottom))"
      : "14px 24px calc(14px + env(safe-area-inset-bottom))",
    animation: mounted ? "brandedHubSlideUp 0.4s cubic-bezier(0.22,1,0.36,1)" : "none",
    transform: mounted ? "none" : "translateY(110%)",
  };

  const keyframes = (
    <>
      <style>{`
        @keyframes brandedHubSlideUp {
          from { opacity: 0; transform: translateY(110%); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {showSignIn && (
        <SignInModal show={showSignIn} onHide={() => setShowSignIn(false)} />
      )}
    </>
  );

  // ---- Mobile ----
  if (isMobile) {
    // Minimised: compact single row (the original size) with a small profile
    // chip and an up-chevron to expand.
    if (!expanded) {
      // Float the expand toggle half-out above the top edge, sitting over the
      // View button — this frees up the horizontal space the inline chevron and
      // avatar used to take. The avatar is dropped entirely when minimised.
      return (
        <>
          {keyframes}
          <div
            role="region"
            aria-label="Branded insights hub"
            style={{ ...wrapperStyle, position: "fixed", overflow: "visible" }}
          >
            <div
              style={{
                position: "absolute",
                top: -15,
                right: 18,
                zIndex: 1,
              }}
            >
              {toggleBtn}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {headlineEl}
                {subtextEl}
              </div>
              {cta}
            </div>
          </div>
        </>
      );
    }

    // Expanded: profile header on top, then the message and a full-width CTA.
    return (
      <>
        {keyframes}
        <div role="region" aria-label="Branded insights hub" style={wrapperStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>{profileGroup(40)}</div>
            {toggleBtn}
          </div>
          {headlineEl}
          {subtextEl}
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            {hasReferral && hasToken
              ? <>
                  {React.cloneElement(claimCta, { style: { ...claimCta.props.style, width: "100%" } })}
                  {React.cloneElement(hubCta, { style: { ...hubCta.props.style, width: "100%" } })}
                </>
              : React.cloneElement(cta, { style: { ...cta.props.style, width: "100%" } })
            }
          </div>
          {samplesBlock && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #e7ebf0" }}>
              {samplesBlock}
            </div>
          )}
        </div>
      </>
    );
  }

  // ---- Desktop: horizontal banner with logo + profile + message + CTA ----
  // Minimised desktop: compact single row (just message + CTA), with the toggle
  // floating above the right edge, matching the mobile minimised treatment.
  if (!expanded) {
    return (
      <>
        {keyframes}
        <div
          role="region"
          aria-label="Branded insights hub"
          style={{ ...wrapperStyle, overflow: "visible" }}
        >
          <div style={{ position: "absolute", top: -15, right: 24, zIndex: 1 }}>
            {toggleBtn}
          </div>
          <div style={{ maxWidth: 1120, margin: "0 auto", display: "flex", alignItems: "center", gap: 18 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {headlineEl}
              {subtextEl}
            </div>
            {cta}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {keyframes}
      <div role="region" aria-label="Branded insights hub" style={{ ...wrapperStyle, overflow: "visible" }}>
        <div style={{ position: "absolute", top: -15, right: 24, zIndex: 1 }}>
          {toggleBtn}
        </div>
        <div style={{ maxWidth: 1120, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            {logoBadge}
            {profileGroup(48)}
            <div
              style={{
                alignSelf: "stretch",
                width: 1,
                background: "#e7ebf0",
                margin: "2px 0",
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              {headlineEl}
              {subtextEl}
            </div>
            {ctaGroup}
          </div>
          {samplesBlock && (
            <div style={{ paddingTop: 12, borderTop: "1px solid #e7ebf0" }}>
              {samplesBlock}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
